import {FC, useMemo} from 'react';
import capitalize from 'lodash/capitalize';
import {MRT_ColumnDef} from 'material-react-table';

import {OrderItemOwnership} from '@entities/order';
import {UserLink} from '@entities/user';
import {Dialog, DialogTitle, MrTable} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {OrderRefLink} from './OrderRefLink';

import {COLOR__PRIMARY_LIGHT} from '@/theme/colors';

const EMPTY_VALUE = '—';

const createColumns = (
  eventId: string,
): MRT_ColumnDef<OrderItemOwnership>[] => [
  {
    accessorKey: 'ownerUserId',
    header: 'Owner user',
    Cell: ({row}) => <UserLink userId={row.original.ownerUserId} />,
    size: 130,
  },
  {
    accessorKey: 'ownerOrderId',
    header: 'Owner order',
    Cell: ({row}) => (
      <OrderRefLink eventId={eventId} orderId={row.original.ownerOrderId} />
    ),
    size: 160,
  },
  {
    accessorKey: 'sellerUserId',
    header: 'Seller user',
    Cell: ({row}) => <UserLink userId={row.original.sellerUserId} />,
    size: 130,
  },
  {
    accessorKey: 'sellerOrderId',
    header: 'Seller order',
    Cell: ({row}) => (
      <OrderRefLink eventId={eventId} orderId={row.original.sellerOrderId} />
    ),
    size: 160,
  },
  {
    accessorKey: 'acquired',
    header: 'Acquired',
    Cell: ({row}) => formatInTz(row.original.acquired, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'released',
    header: 'Released',
    Cell: ({row}) => {
      const {released} = row.original;

      return released ? formatInTz(released, DATETIME_FORMAT) : EMPTY_VALUE;
    },
    size: 160,
  },
  {
    accessorKey: 'releaseReason',
    header: 'Release reason',
    Cell: ({row}) => {
      const {releaseReason} = row.original;

      return releaseReason ? capitalize(releaseReason) : EMPTY_VALUE;
    },
    size: 140,
  },
  {
    accessorKey: 'releaseOrderId',
    header: 'Release order',
    Cell: ({row}) => (
      <OrderRefLink eventId={eventId} orderId={row.original.releaseOrderId} />
    ),
    size: 160,
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  ownership: OrderItemOwnership[];
}

export const OwnershipDialog: FC<Props> = ({
  isOpen,
  onClose,
  eventId,
  ownership,
}) => {
  const columns = useMemo(() => createColumns(eventId), [eventId]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      // The Paper hugs the table's own width (sum of its column sizes),
      // capped so it never exceeds the viewport
      sx={{'& .MuiPaper-root': {width: 'fit-content', maxWidth: '98vw'}}}
    >
      <DialogTitle title="Ownership" onClose={onClose} />

      <MrTable
        columns={columns}
        data={ownership}
        enablePagination={false}
        // Highlights the ownership record the order being viewed took part in
        muiTableBodyRowProps={({row}) => ({
          sx: {
            backgroundColor: row.original.isCurrentOrder
              ? COLOR__PRIMARY_LIGHT
              : undefined,
          },
        })}
        {...entityTableCommonProps}
        enableRowActions={false}
        // entityTableCommonProps sizes this for the dashboard layout (minus
        // the sidebar); inside a dialog there's no sidebar to account for
        muiTableContainerProps={{
          sx: {
            maxWidth: '100%',
            overflowX: 'auto',
          },
        }}
      />
    </Dialog>
  );
};
